import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import OverviewCards from '../components/dashboard/overview/OverviewCards';
import QuickInsights from '../components/dashboard/overview/QuickInsights';
import SmartCardsPreview from '../components/dashboard/overview/SmartCardsPreview';
import MudadCompliance from '../components/dashboard/overview/MudadCompliance';
import { useLanguage } from '../contexts/LanguageContext';

const DashboardOverview: React.FC = () => {
  const { t } = useLanguage();

  // Mock user data - في التطبيق الحقيقي سيأتي من API
  const userCompany = {
    name: t('dashboard.company_name'),
    logo: null,
    industry: 'تجارة التجزئة',
    employees: 145,
    establishedYear: 2018
  };

  const currentTime = new Date().toLocaleTimeString('ar-SA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  const currentDate = new Date().toLocaleDateString('ar-SA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <DashboardLayout currentPage="overview">
      <div className="p-6 space-y-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-20 -translate-y-20"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full translate-x-16 translate-y-16"></div>
            <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-white rounded-full -translate-x-12 -translate-y-12"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {t('dashboard.welcome')} {userCompany.name}
                </h1>
                <p className="text-blue-100 text-lg">
                  {currentDate} - {currentTime}
                </p>
              </div>
              <div className="text-right">
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-sm text-blue-100 mb-1">نوع النشاط</p>
                  <p className="font-semibold">{userCompany.industry}</p>
                  <p className="text-sm text-blue-100 mt-2">{userCompany.employees} موظف</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <h3 className="text-sm font-medium text-blue-100 mb-1">حالة النظام</h3>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full ml-2 rtl:ml-0 rtl:mr-2"></div>
                  <span className="font-semibold">{t('dashboard.connected_updated')}</span>
                </div>
              </div>
              
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <h3 className="text-sm font-medium text-blue-100 mb-1">{t('dashboard.last_sync')}</h3>
                <p className="font-semibold">منذ 3 دقائق</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <h3 className="text-sm font-medium text-blue-100 mb-1">{t('dashboard.connected_banks')}</h3>
                <p className="font-semibold">3 {t('dashboard.banks_connected')}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Overview Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('dashboard.financial_overview')}
          </h2>
          <OverviewCards />
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - Insights */}
          <div className="xl:col-span-2">
            <QuickInsights />
          </div>

          {/* Right Column - Smart Cards */}
          <div className="xl:col-span-1">
            <SmartCardsPreview />
          </div>
        </div>

        {/* Mudad Compliance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('dashboard.compliance')} والامتثال التنظيمي
          </h2>
          <MudadCompliance />
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700"
        >
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            {t('dashboard.quick_actions')}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                title: 'إنشاء بطاقة ذكية',
                description: 'قسم أو مشروع جديد',
                icon: '🎯',
                color: 'from-blue-500 to-blue-600',
                href: '/dashboard/cards/new'
              },
              {
                title: 'طلب تمويل فوري',
                description: 'خلال 6 ساعات',
                icon: '⚡',
                color: 'from-green-500 to-green-600',
                href: '/dashboard/financing'
              },
              {
                title: 'استكشاف الشراكات',
                description: 'خصومات وعروض',
                icon: '🤝',
                color: 'from-purple-500 to-purple-600',
                href: '/dashboard/partnerships'
              },
              {
                title: 'تقرير شامل',
                description: 'تحليل مفصل',
                icon: '📊',
                color: 'from-orange-500 to-orange-600',
                href: '/dashboard/analytics'
              }
            ].map((action, index) => (
              <motion.div
                key={action.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <Link to={action.href} className="block w-full h-full">
                <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className="text-3xl mb-3">{action.icon}</div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {action.description}
                  </p>
                </div>

                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${action.color} rounded-b-xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-sm text-gray-500 dark:text-gray-400 py-4"
        >
          <p>
            جميع البيانات محمية ومشفرة وفقاً لمعايير SAMA و PDPL
          </p>
          <p className="mt-1">
            آخر تحديث للبيانات: {currentTime} | إصدار النظام: 2.1.0
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardOverview;
